import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ScrollText, ShieldCheck, Plus, Check, ClipboardList, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_LABELS = {
  terms: "Terms of Service",
  privacy: "Privacy Notice",
  hipaa: "HIPAA Notice",
};

export default function LegalConsentAdmin() {
  const [versions, setVersions] = useState([]);
  const [consents, setConsents] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    doc_type: "terms",
    version: "",
    title: "Terms of Service",
    body_markdown: "",
    effective_date: new Date().toISOString().slice(0, 10),
    publish_now: true,
  });
  const [filters, setFilters] = useState({ email: "", doc_type: "", version: "" });

  const loadVersions = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/legal/versions`);
    setVersions(data);
  }, []);

  const loadConsents = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.email) params.set("email", filters.email);
    if (filters.doc_type) params.set("doc_type", filters.doc_type);
    if (filters.version) params.set("version", filters.version);
    params.set("limit", "50");
    const { data } = await axios.get(`${API}/admin/legal/consents?${params.toString()}`);
    setConsents(data);
  }, [filters]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadVersions(), loadConsents()]);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadVersions, loadConsents]);

  const handlePublishExisting = async (v) => {
    if (!window.confirm(`Publish ${v.doc_type} v${v.version} as the current version? Any users who accepted an older version will be prompted to re-accept.`)) return;
    try {
      await axios.post(`${API}/admin/legal/versions/${v.id}/publish`);
      toast.success(`${DOC_LABELS[v.doc_type]} v${v.version} is now the current published version.`);
      await loadVersions();
    } catch (e) {
      toast.error("Failed to publish version");
    }
  };

  const handleCreateVersion = async (e) => {
    e.preventDefault();
    if (!form.version || !form.title || !form.body_markdown || !form.effective_date) {
      toast.error("Version, title, effective date, and body are required.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/legal/versions`, form);
      toast.success(`${DOC_LABELS[form.doc_type]} v${form.version} ${form.publish_now ? "published" : "saved as draft"}.`);
      setShowEditor(false);
      setForm({ ...form, version: "", body_markdown: "" });
      await loadVersions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save version");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="admin-legal-section"
      className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-primary" />
          <div>
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Legal & Consent</h2>
            <p className="text-sm font-body text-text-secondary">
              Publish versioned Terms &amp; Privacy notices and audit every family's acceptance.
            </p>
          </div>
        </div>
        <Button
          data-testid="admin-legal-new-version-btn"
          onClick={() => setShowEditor((s) => !s)}
          className="bg-primary hover:bg-primary-dark text-white rounded-full font-body font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" /> {showEditor ? "Cancel" : "Publish new version"}
        </Button>
      </div>

      {showEditor && (
        <form
          data-testid="admin-legal-editor"
          onSubmit={handleCreateVersion}
          className="border border-[#E2E8F0] rounded-xl p-5 mb-8 bg-[#FAFBFC] grid gap-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-text-secondary font-body">Doc type</Label>
              <select
                data-testid="admin-legal-doc-type"
                value={form.doc_type}
                onChange={(e) => {
                  const t = e.target.value;
                  setForm((f) => ({ ...f, doc_type: t, title: DOC_LABELS[t] || f.title }));
                }}
                className="w-full mt-1 rounded-lg border border-[#E2E8F0] px-3 py-2 font-body text-sm bg-white"
              >
                <option value="terms">Terms of Service</option>
                <option value="privacy">Privacy Notice</option>
                <option value="hipaa">HIPAA Notice</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-text-secondary font-body">Version (e.g. 2.0)</Label>
              <Input
                data-testid="admin-legal-version-input"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="2.0"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-text-secondary font-body">Effective date</Label>
              <Input
                type="date"
                data-testid="admin-legal-effective-input"
                value={form.effective_date}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-text-secondary font-body">Title</Label>
            <Input
              data-testid="admin-legal-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-text-secondary font-body">
              Body (Markdown)
            </Label>
            <Textarea
              data-testid="admin-legal-body-input"
              value={form.body_markdown}
              onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
              rows={12}
              className="mt-1 font-mono text-sm"
              placeholder="# Terms of Service&#10;&#10;## 1. Section..."
            />
            <p className="text-xs text-text-secondary font-body mt-1">
              Supports standard Markdown — H1/H2/H3, lists, links, bold. Line breaks preserved.
            </p>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-body text-text-primary">
              <Switch
                data-testid="admin-legal-publish-toggle"
                checked={form.publish_now}
                onCheckedChange={(v) => setForm({ ...form, publish_now: v })}
              />
              Publish immediately (prompts everyone to re-accept)
            </label>
            <Button
              type="submit"
              data-testid="admin-legal-save-btn"
              disabled={saving}
              className="bg-primary hover:bg-primary-dark text-white rounded-full font-body font-semibold"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save version"}
            </Button>
          </div>
        </form>
      )}

      {/* Versions table */}
      <div className="mb-10">
        <h3 className="text-sm font-body font-bold uppercase tracking-wider text-text-secondary mb-3">
          Published & draft versions
        </h3>
        {loading ? (
          <div className="text-sm text-text-secondary font-body">Loading…</div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-text-secondary font-body">No versions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body" data-testid="admin-legal-versions-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-text-secondary border-b border-[#E2E8F0]">
                  <th className="py-2 pr-4">Doc</th>
                  <th className="py-2 pr-4">Version</th>
                  <th className="py-2 pr-4">Effective</th>
                  <th className="py-2 pr-4">Published</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => {
                  const Icon = v.doc_type === "privacy" ? ShieldCheck : ScrollText;
                  return (
                    <tr key={v.id} className="border-b border-[#F1F5F9]" data-testid={`admin-legal-row-${v.doc_type}-${v.version}`}>
                      <td className="py-2.5 pr-4 flex items-center gap-2 text-text-primary font-semibold">
                        <Icon className="w-4 h-4 text-primary" /> {DOC_LABELS[v.doc_type] || v.doc_type}
                      </td>
                      <td className="py-2.5 pr-4">v{v.version}</td>
                      <td className="py-2.5 pr-4 text-text-secondary">{v.effective_date}</td>
                      <td className="py-2.5 pr-4 text-text-secondary">
                        {v.published_at ? new Date(v.published_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2.5 pr-4">
                        {v.is_current ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-primary/10 text-primary">
                            <Check className="w-3 h-3" /> Current
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#F1F5F9] text-[#475569]">
                            Archived
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 text-right">
                        {!v.is_current && (
                          <Button
                            data-testid={`admin-legal-publish-${v.doc_type}-${v.version}`}
                            variant="outline"
                            size="sm"
                            onClick={() => handlePublishExisting(v)}
                            className="rounded-full"
                          >
                            Republish
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Consent audit */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-body font-bold uppercase tracking-wider text-text-secondary">
            Consent audit log
          </h3>
          <span className="text-xs text-text-secondary font-body">{consents.total} total records</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Input
            data-testid="admin-legal-filter-email"
            placeholder="Filter by email"
            value={filters.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
          />
          <select
            data-testid="admin-legal-filter-type"
            value={filters.doc_type}
            onChange={(e) => setFilters({ ...filters, doc_type: e.target.value })}
            className="rounded-lg border border-[#E2E8F0] px-3 py-2 font-body text-sm bg-white"
          >
            <option value="">All doc types</option>
            <option value="terms">Terms of Service</option>
            <option value="privacy">Privacy Notice</option>
            <option value="hipaa">HIPAA Notice</option>
          </select>
          <Input
            data-testid="admin-legal-filter-version"
            placeholder="Version (e.g. 1.0)"
            value={filters.version}
            onChange={(e) => setFilters({ ...filters, version: e.target.value })}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body" data-testid="admin-legal-consents-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-text-secondary border-b border-[#E2E8F0]">
                <th className="py-2 pr-4">Accepted</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Doc</th>
                <th className="py-2 pr-4">Version</th>
                <th className="py-2 pr-4">Context</th>
                <th className="py-2 pr-4">IP</th>
              </tr>
            </thead>
            <tbody>
              {consents.rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-text-secondary italic">
                    No consent records match your filters yet.
                  </td>
                </tr>
              ) : (
                consents.rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#F1F5F9]">
                    <td className="py-2.5 pr-4 text-text-secondary">
                      {new Date(r.accepted_at).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-4 font-semibold text-text-primary">{r.email}</td>
                    <td className="py-2.5 pr-4 capitalize">{DOC_LABELS[r.doc_type] || r.doc_type}</td>
                    <td className="py-2.5 pr-4">v{r.version}</td>
                    <td className="py-2.5 pr-4">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#F1F5F9] text-[#475569]">
                        {r.context}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-text-secondary font-mono text-xs">{r.ip_address}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
